import { useEffect, useState } from 'react';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { CandidatoVentaVista } from '@shared/types';

/**
 * Sprint 109 — "No me deja ingresar productos para armar paquetes".
 *
 * ## Qué le pasó al negocio
 *
 * Capturó Pepsi y las Coca Cola **desde Inventario** —*"que ya está en el inventario"*, dijo— y al
 * ir a armar un paquete no aparecían. El armador ofrece **productos**, y lo que ella creó son
 * ingredientes: existen, se cuentan, ocupan lugar en el almacén… y no se pueden vender.
 *
 * El sistema lo sabía desde el primer segundo y **no dijo nada**. Ése es el defecto: no que
 * existieran los dos conceptos, sino que uno se pudiera crear sin que nada avisara que no sirve
 * para vender. La quedó buscando un botón que nunca iba a aparecer.
 *
 * ## Por qué aquí
 *
 * Este aviso vive en Inventario porque es donde está mirando quien tiene el problema. El mecanismo
 * que los detecta existe desde el Sprint 72 (`candidatosVenta`) y la pantalla para repararlos
 * también — pero había que **saber que existen** y entrar a buscarlas en un menú de Productos
 * llamado "Otras formas de agregar". Un diagnóstico que no se ofrece solo no diagnostica nada.
 *
 * No aparece si no hay nada roto: un tablero que siempre tiene un aviso encima deja de leerse.
 */
export function AvisoInvendibles() {
  const navigate = useNavigate();
  const [invendibles, setInvendibles] = useState<CandidatoVentaVista[]>([]);

  useEffect(() => {
    void (async () => {
      const r = await window.api.productos.candidatosVenta();
      if (r.ok) setInvendibles(r.data);
    })();
  }, []);

  if (invendibles.length === 0) return null;

  const uno = invendibles.length === 1;
  // Tres nombres bastan para que se reconozca cuál es; la lista completa está en la pantalla que abre.
  const muestra = invendibles.slice(0, 3).map((i) => i.nombre).join(', ');
  const resto = invendibles.length - Math.min(3, invendibles.length);

  return (
    <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1">
          <h2 className="font-bold text-amber-900">
            {uno
              ? 'Tienes 1 ingrediente que todavía no se puede vender'
              : `Tienes ${invendibles.length} ingredientes que todavía no se pueden vender`}
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-amber-900">
            <strong>{muestra}</strong>
            {resto > 0 && ` y ${resto} más`} {uno ? 'está' : 'están'} en tu almacén, pero{' '}
            {uno ? 'no aparece' : 'no aparecen'} en el punto de venta, ni en el menú en línea, ni
            para armar paquetes: {uno ? 'es un ingrediente' : 'son ingredientes'} y todavía nadie
            dijo a cómo {uno ? 'se vende' : 'se venden'}.
          </p>
          <button
            onClick={() => navigate('/productos?reparar=invendibles')}
            className="btn-primary mt-3 min-h-[44px]"
          >
            Ponerlos a la venta
            <ArrowRight className="h-4 w-4" />
          </button>
          <p className="mt-2 text-xs text-amber-800">
            Sólo hay que ponerles precio. Si {uno ? 'es' : 'son'} de los que{' '}
            {uno ? 'se usa' : 'se usan'} para preparar otra cosa —el queso de una torta— déjalo así:
            está bien que no {uno ? 'se venda' : 'se vendan'} solo.
          </p>
        </div>
      </div>
    </div>
  );
}
